import { Link } from 'react-router'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { Logo } from '@/components/Logo'
import { useI18n } from '@/i18n/i18n'

type Section = { h: string; p: string[] }

const PRIVACY: Record<'zh-Hant' | 'en', { title: string; updated: string; sections: Section[] }> = {
  'zh-Hant': {
    title: '隱私權政策',
    updated: '最後更新：2026 年 10 月 12 日',
    sections: [
      {
        h: '我們收集什麼',
        p: [
          '使用 Google 登入時，我們取得你的名字、email 與大頭貼網址，用來建立和識別你的帳號。',
          '你在白板上建立的內容（卡片、訊息、上傳的檔案與圖片）、對話紀錄，以及 AI 的使用量與費用紀錄。',
          '為了維持服務運作的基本技術紀錄，例如登入 session、IP 位址與瀏覽器資訊。',
        ],
      },
      {
        h: '我們怎麼使用',
        p: [
          '只用來提供 moot 的功能：保存你的白板、讓 AI 協助你、計算用量。',
          '當你與 AI 互動時，該 session 的對話與白板內容會傳送給 Anthropic 的 Claude API 處理。Anthropic 依其商業條款處理這些資料，預設不會用於訓練模型。',
          '我們不販售你的資料，也不用於廣告。',
        ],
      },
      {
        h: '資料存放',
        p: [
          '資料存放在 Cloudflare 的基礎設施上（Workers、D1、Durable Objects、R2）。',
          '你可以隨時刪除白板。刪除帳號與所有資料的請求，請透過登入用的同一個 email 與我們聯絡。',
        ],
      },
      {
        h: 'Cookie',
        p: ['我們只使用維持登入狀態所必需的 cookie，以及在你的瀏覽器裡記住外觀與語言偏好。'],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: October 12, 2026',
    sections: [
      {
        h: 'What we collect',
        p: [
          'When you sign in with Google we receive your name, email address and profile picture URL, used to create and identify your account.',
          'The content you create on boards (cards, messages, uploaded files and images), your conversations, and records of AI usage and cost.',
          'Basic technical records needed to run the service, such as sign-in sessions, IP addresses and browser information.',
        ],
      },
      {
        h: 'How we use it',
        p: [
          'Only to provide moot: saving your boards, letting the AI help you, and accounting for usage.',
          "When you interact with the AI, that session's conversation and board content are sent to Anthropic's Claude API for processing. Anthropic handles this data under its commercial terms and does not train models on it by default.",
          'We do not sell your data or use it for advertising.',
        ],
      },
      {
        h: 'Storage',
        p: [
          "Data is stored on Cloudflare's infrastructure (Workers, D1, Durable Objects, R2).",
          'You can delete boards at any time. To delete your account and all data, contact us from the email address you sign in with.',
        ],
      },
      {
        h: 'Cookies',
        p: [
          'We only use the cookies needed to keep you signed in, plus browser storage for your appearance and language preferences.',
        ],
      },
    ],
  },
}

const TERMS: Record<'zh-Hant' | 'en', { title: string; updated: string; sections: Section[] }> = {
  'zh-Hant': {
    title: '服務條款',
    updated: '最後更新：2026 年 10 月 12 日',
    sections: [
      {
        h: '服務',
        p: [
          'moot 目前處於早期階段，依現況提供，不保證不中斷或沒有錯誤。功能與條款可能調整。',
          'AI 產生的內容可能有誤，重要決定請自行查證。',
        ],
      },
      {
        h: '你的內容',
        p: [
          '你上傳與建立的內容屬於你。你授權我們為了提供服務而儲存、處理並傳送給 AI 供應商。',
          '請勿上傳你無權使用的內容，或利用服務從事違法行為。',
        ],
      },
      {
        h: '費用',
        p: [
          '新帳號有試用額度。用完後可以使用你自己的 Anthropic API key，相關費用由你與 Anthropic 結算。',
        ],
      },
    ],
  },
  en: {
    title: 'Terms of Service',
    updated: 'Last updated: October 12, 2026',
    sections: [
      {
        h: 'The service',
        p: [
          'moot is at an early stage and provided as is, without guarantees of uninterrupted or error-free operation. Features and these terms may change.',
          'AI-generated content can be wrong. Verify anything important yourself.',
        ],
      },
      {
        h: 'Your content',
        p: [
          'What you upload and create is yours. You allow us to store and process it, and to send it to our AI provider, in order to run the service.',
          'Do not upload content you have no right to use, or use the service for anything unlawful.',
        ],
      },
      {
        h: 'Cost',
        p: [
          'New accounts include trial credit. After that you can use your own Anthropic API key, billed between you and Anthropic.',
        ],
      },
    ],
  },
}

function LegalPage({ doc }: { doc: typeof PRIVACY }) {
  const { locale } = useI18n()
  const d = doc[locale]
  return (
    <div className="min-h-full">
      <header className="flex h-14 items-center justify-between border-b border-line px-4">
        <Link to="/" className="rounded-md">
          <Logo />
        </Link>
        <LanguageSwitch />
      </header>
      <main className="mx-auto max-w-2xl px-4 pt-12 pb-24 sm:px-6">
        <h1 className="text-[28px] font-semibold tracking-[-0.025em]">{d.title}</h1>
        <p className="mt-1 text-[13px] text-muted">{d.updated}</p>
        {d.sections.map((s) => (
          <section key={s.h} className="mt-9">
            <h2 className="text-[17px] font-semibold">{s.h}</h2>
            {s.p.map((p) => (
              <p key={p} className="mt-2.5 text-[15px] leading-7 text-ink-2">
                {p}
              </p>
            ))}
          </section>
        ))}
      </main>
    </div>
  )
}

export function Privacy() {
  return <LegalPage doc={PRIVACY} />
}

export function Terms() {
  return <LegalPage doc={TERMS} />
}
